import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Home, Dumbbell, FlaskConical, type LucideIcon } from 'lucide-react'

interface NavItem {
  path: string
  label: string
  icon: LucideIcon
  matchPaths?: string[]
}

const navItems: NavItem[] = [
  {
    path: '/',
    label: 'Dashboard',
    icon: Home,
  },
  {
    path: '/logbook',
    label: 'Logbook',
    icon: Dumbbell,
    matchPaths: ['/logbook', '/workout'],
  },
  {
    path: '/cycle',
    label: 'Cycle',
    icon: FlaskConical,
  },
]

export default function BottomNav() {
  const location = useLocation()

  const isActive = (item: NavItem) => {
    if (item.matchPaths) {
      return item.matchPaths.some((p) => location.pathname.startsWith(p))
    }
    return location.pathname === item.path
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 safe-bottom">
      {/* Glass background */}
      <div className="absolute inset-0 bg-surface/80 backdrop-blur-xl border-t border-white/5" />

      {/* Subtle top glow */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="relative flex items-center justify-around px-6 py-2">
        {navItems.map((item) => {
          const active = isActive(item)
          const Icon = item.icon

          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={cn(
                'relative flex flex-col items-center justify-center gap-0.5 min-w-[64px] py-2 px-4 rounded-2xl transition-all duration-200',
                'active:scale-95',
                active
                  ? 'text-primary'
                  : 'text-text-muted hover:text-text-secondary'
              )}
            >
              <Icon
                className={cn(
                  'w-6 h-6 transition-all duration-200',
                  active ? 'scale-110' : 'scale-100'
                )}
                fill={active ? 'currentColor' : 'none'}
                strokeWidth={1.5}
              />
              <span
                className={cn(
                  'text-[10px] font-semibold tracking-wide uppercase transition-all duration-200',
                  active ? 'text-primary opacity-100' : 'text-text-muted opacity-70'
                )}
              >
                {item.label}
              </span>

              {/* Active dot indicator */}
              {active && (
                <div className="absolute -bottom-0.5 w-1 h-1 bg-primary rounded-full" />
              )}
            </NavLink>
          )
        })}
      </div>
    </nav>
  )
}
